import React from "react";
import classnames from "classnames";

import { Tooltip } from "../Tooltip";

export type IconType =
  | "sort"
  | "export"
  | "sort-asc"
  | "sort-dsc"
  | "clear"
  | "find"
  | "white-clear"
  | "eye"
  | "eye-close"
  | "arrow-left"
  | "warn"
  | "logout"
  | "apply"
  | "delete"
  | "delete-red"
  | "loupe"
  | "btn-close"
  | "btn-open"
  | "google"
  | "back"
  | "user"
  | "chevron-left"
  | "arrow-long"
  | "survey-list"
  | "goals"
  | "goals-empty"
  | "procedures-empty"
  | "edit"
  | "edit-blue"
  | "progress-success"
  | "danger"
  | "close"
  | "not-found"
  | "procedure-list"
  | "team-filter"
  | "project-filter"
  | "core-value-without-score"
  | "core-value-score-20"
  | "core-value-score-15"
  | "core-value-score-10"
  | "core-value-score-5"
  | "arrow-forward"
  | "recognitions-empty"
  | "filter"
  | "arrow-down"
  | "finish-onboarding"
  | "stop"
  | "copy"
  | "thumbs-up"
  | "thumbs-down"
  | "zap"
  | "wallet"
  | "clock";

export interface IconProps {
  type: IconType;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Accessible name AND tooltip text for an icon-only control.
   *  Only takes effect together with `onClick` -- a decorative icon (no click handler) stays a plain
   *  div, and `label` alone would create a focusable control that does nothing on activation. */
  label?: string;
}

/**
 * A CSS-background glyph (`type` selects the image via the shared `.icon` SCSS). Decorative by
 * default (a bare `<div>`, unreachable by keyboard, no accessible name) -- pass both `label` and
 * `onClick` to make it a real, focusable, tooltipped control instead; every other caller's markup is
 * unchanged.
 *
 * @param props.type - selects the glyph via the `.icon.{type}` SCSS rule.
 * @param props.className - merged onto the glyph element's class list.
 * @param props.onClick - click handler. Combined with `label`, upgrades the glyph to a `<button>`.
 * @param props.label - accessible name and tooltip text; has no effect without `onClick`.
 */
export default function Icon(props: IconProps) {
  const { type, className, onClick, label } = props;
  const glyphClassName = classnames("icon", className, type);

  if (label && onClick) {
    return (
      // `block`: `.icon` is `width/height: 100%` of whatever fixed- or intrinsically-sized box wraps
      // it (`.item-icon`, `.search-icon-block`, etc.) -- the anchor span Tooltip inserts must pass
      // that size through, or the button collapses to its own (empty) content size instead.
      <Tooltip text={label} block>
        <button type="button" aria-label={label} onClick={onClick} className={glyphClassName} />
      </Tooltip>
    );
  }

  return <div onClick={onClick} className={glyphClassName} />;
}
